import { Spinner } from "@components/ui-kit";
import { useGetRequestsForInvoicingQuery } from "@src/__generated__/graphql";
import {
  useGetRequestsForInvoicingSummaryQuery,
  useRequestsForInvoicingFiltersQuery,
} from "@src/__generated__/urql-graphql";
import { withCan } from "@src/utils/components/permissions";
import { useUpdateURI } from "@src/utils/hooks/useUpdateURI";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { CardListRequestsForInvoice } from "./CardListRequestsForInvoice";
import { StoreRequestsForInvoicing } from "./StoreRequestsForInvoicing";

export const PageRequestsForInvoicingCtx = React.createContext(
  {} as StoreRequestsForInvoicing,
);

function useData(store: StoreRequestsForInvoicing) {
  const { loading, error, refetch } = useGetRequestsForInvoicingQuery({
    notifyOnNetworkStatusChange: true,
    variables: store.queryParams,
    onCompleted(data) {
      store.fromGetRequestsForInvoicingQuery(data);
    },
  });
  const [{ data: invoiceSummary, fetching }, refetchSummary] =
    useGetRequestsForInvoicingSummaryQuery({
      variables: store.queryParams,
    });

  const [{ data: filterOptions }] = useRequestsForInvoicingFiltersQuery();

  useEffect(() => {
    if (!invoiceSummary) return;
    store.setAllInvoicingIds(invoiceSummary);
  }, [invoiceSummary]);

  useEffect(() => {
    if (!filterOptions) return;
    store.setFilterOptions(filterOptions);
  }, [filterOptions]);

  store.refetch = () => {
    refetch();
    refetchSummary();
  };

  return { loading: loading || fetching, error };
}

export const PageRequestsForInvoicing = withCan(
  {
    do: [
      "requestForInvoicing_read_all",
      "requestForInvoicing_read_own",
      "requestForInvoicing_read_team",
    ],
  },
  observer(function PageRequestsForInvoicing() {
    const { query } = useRouter();

    const [store] = useState(() => {
      const store = StoreRequestsForInvoicing.create();
      store.where.fromURLSearchParam(query);
      store.orderBy.fromURLSearchParam(query);
      store.pagination.fromURLSearchParam(query);
      return store;
    });

    useUpdateURI(store.searchParams);

    const { loading } = useData(store);

    return (
      <PageRequestsForInvoicingCtx.Provider value={store}>
        <div className="flex justify-center items-center">
          <div className="w-full">
            {loading && <Spinner absolute />}
            <CardListRequestsForInvoice />
          </div>
        </div>
      </PageRequestsForInvoicingCtx.Provider>
    );
  }),
);
